import { useParams, Link } from 'react-router';
import { Button } from '@lms/ui';
import {
  ArrowLeft,
  FileText,
  CheckCircle2,
  Loader2,
  CheckCircle,
  MessageSquare,
  Send,
  Sparkles,
  AlertCircle,
  ArrowRight,
  Lock,
  Music,
  ExternalLink,
  Download,
  Video,
  Globe,
} from 'lucide-react';
import { useState } from 'react';
import { useMaterial, useCompleteMaterial } from '../hooks/useMaterials';
import { useEnrollments } from '../hooks/useEnrollments';
import { useCreateReflection } from '../hooks/useRegistrations';
import { useAuthStore } from '../store/auth';
import { MediaPlayer } from '../components/MediaPlayer';
import type { Enrollment, MaterialMedia } from '@lms/shared/types';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { CurriculumPath, type CurriculumPathItem } from '../components/CurriculumPath';

export function MaterialDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const { data: material, isLoading: materialLoading, error } = useMaterial(id || '');
  const { data: enrollments, isLoading: enrollmentsLoading } = useEnrollments({ userId: user?.id });
  const { mutate: complete, isPending: completePending } = useCompleteMaterial();
  const { mutate: submitReflection, isPending: submittingReflection } = useCreateReflection();
  const { t } = useLanguage();

  const [reflectionText, setReflectionText] = useState('');
  const [showReflectionForm, setShowReflectionForm] = useState(false);

  // Find which program this material belongs to across 6-Tier curriculum or legacy flows
  const currentEnrollment = enrollments?.find((e: Enrollment) => {
    const inStages = e.program?.levels?.some(l =>
      l.stages?.some(s =>
        s.subjects?.some(sub => sub.sessions?.some(ses => ses.materials?.some(m => m.id === id)))
      )
    );
    if (inStages) return true;

    const inLevelFlows = e.program?.levels?.some(l =>
      l.flows?.some(f => f.materials?.some(m => m.id === id))
    );
    return !!inLevelFlows;
  });

  const program = currentEnrollment?.program;

  // Find current session, subject, stage, level context
  const getSessionHierarchy = () => {
    if (!program?.levels) return null;
    for (const lvl of program.levels) {
      for (const stg of lvl.stages || []) {
        for (const sbj of stg.subjects || []) {
          const sessions = sbj.sessions || [];
          for (let i = 0; i < sessions.length; i++) {
            const ses = sessions[i];
            const sessionMaterials = ses.materials || [];
            const materialIndex = sessionMaterials.findIndex(m => m.id === id);
            if (materialIndex >= 0) {
              const prevSes = i > 0 ? sessions[i - 1] : null;
              const nextSes = i < sessions.length - 1 ? sessions[i + 1] : null;
              const previousMaterial =
                sessionMaterials[materialIndex - 1] ?? prevSes?.materials?.at(-1) ?? null;
              const followingMaterial =
                sessionMaterials[materialIndex + 1] ?? nextSes?.materials?.[0] ?? null;
              return {
                level: lvl,
                stage: stg,
                subject: sbj,
                session: ses,
                previousMaterial,
                followingMaterial,
                previousMaterialSession: materialIndex > 0 ? ses : prevSes,
                followingMaterialSession:
                  materialIndex < sessionMaterials.length - 1 ? ses : nextSes,
              };
            }
          }
        }
      }
    }
    return null;
  };

  const sessionHierarchy = getSessionHierarchy();
  const curriculumReturnUrl =
    program && sessionHierarchy
      ? `/programs/${program.id}#session-${sessionHierarchy.session.id}`
      : program
        ? `/programs/${program.id}`
        : '/programs';

  if (materialLoading || enrollmentsLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
        <p className="text-sm text-on-surface-variant font-body">
          {t(copy.materialDetail.loading)}
        </p>
      </div>
    );
  }

  const isPreviousMaterialNotCompleted =
    error instanceof Error &&
    (error.message.includes('PREVIOUS') ||
      error.message.includes('PREREQUISITE') ||
      error.message.includes('QUOTA'));

  if (error || isPreviousMaterialNotCompleted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8 bg-surface-container-low rounded-3xl border-2 border-dashed border-primary/10">
        <div className="size-20 rounded-full bg-primary/5 flex items-center justify-center mb-6">
          <Lock size={40} className="text-primary/20" />
        </div>
        <h2 className="text-2xl font-headline font-bold text-on-surface mb-2">
          {t(copy.materialDetail.lockedTitle)}
        </h2>
        <p className="text-on-surface-variant max-w-md mx-auto mb-8 font-body leading-relaxed">
          {error instanceof Error ? error.message : t(copy.materialDetail.lockedDesc)}
        </p>
        <Link to={curriculumReturnUrl} className="no-underline">
          <Button className="gap-2">
            <ArrowLeft size={18} />
            {t(copy.materialDetail.backToCurriculum)}
          </Button>
        </Link>
      </div>
    );
  }

  if (!material) {
    return (
      <div className="text-center py-12">
        <p className="text-on-surface-variant">{t(copy.materialDetail.notFound)}</p>
      </div>
    );
  }

  const isCompleted = currentEnrollment?.completions?.some(c => c.materialId === material.id);
  const curriculumPathItems: CurriculumPathItem[] = sessionHierarchy
    ? [
        {
          key: 'program',
          label: t(copy.curriculum.program),
          value: program?.name || t(copy.curriculum.program),
          href: program ? `/programs/${program.id}` : '/programs',
        },
        {
          key: 'level',
          label: t(copy.curriculum.level),
          value: sessionHierarchy.level.name,
          href: program ? `/programs/${program.id}#level-${sessionHierarchy.level.id}` : undefined,
        },
        {
          key: 'stage',
          label: t(copy.curriculum.stage),
          value: sessionHierarchy.stage.name,
          href: program ? `/programs/${program.id}#stage-${sessionHierarchy.stage.id}` : undefined,
        },
        {
          key: 'subject',
          label: t(copy.curriculum.subject),
          value: sessionHierarchy.subject.name,
          href: program
            ? `/programs/${program.id}#subject-${sessionHierarchy.subject.id}`
            : undefined,
        },
        {
          key: 'session',
          label: t(copy.curriculum.session),
          value: `#${sessionHierarchy.session.sessionNumber} ${sessionHierarchy.session.title}`,
          href: curriculumReturnUrl,
        },
        {
          key: 'material',
          label: t(copy.curriculum.material),
          value: material.title,
          current: true,
        },
      ]
    : [
        {
          key: 'program',
          label: t(copy.curriculum.program),
          value: program?.name || t(copy.curriculum.program),
          href: program ? `/programs/${program.id}` : '/programs',
        },
        {
          key: 'material',
          label: t(copy.curriculum.material),
          value: material.title,
          current: true,
        },
      ];

  const handleComplete = () => {
    if (currentEnrollment && id) {
      void complete(
        { id, enrollmentId: currentEnrollment.id },
        {
          onSuccess: () => {
            setShowReflectionForm(true);
          },
        }
      );
    }
  };

  const handleReflectionSubmit = () => {
    if (!user || !reflectionText.trim()) return;
    void submitReflection(
      {
        materialId: id,
        userId: user.id,
        prompt: t(copy.materialDetail.reflectionPrompt),
        response: reflectionText,
        programId: program?.id,
      },
      {
        onSuccess: () => {
          setReflectionText('');
          setShowReflectionForm(false);
        },
      }
    );
  };

  return (
    <div className="space-y-8 animate-fadeIn pb-12">
      {/* Breadcrumb & Back Navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to={curriculumReturnUrl}
          className="inline-flex items-center gap-2 rounded-xl bg-surface-container px-3 py-2 font-body text-on-surface-variant no-underline transition-colors hover:bg-surface-container-high hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
        >
          <ArrowLeft size={16} />
          <span className="text-xs font-bold uppercase tracking-wider">
            {t(copy.materialDetail.backToCurriculum)}
          </span>
        </Link>
      </div>

      <CurriculumPath
        ariaLabel={t(copy.curriculum.ariaLabel)}
        eyebrow={t(copy.curriculum.currentPosition)}
        items={curriculumPathItems}
      />

      {/* Header Area */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-label font-bold text-primary bg-primary/5 px-2 py-0.5 rounded tracking-widest uppercase">
              {material.type}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-headline font-bold text-on-surface tracking-tight leading-tight">
            {material.title}
          </h1>
        </div>
        {isCompleted && (
          <div className="flex items-center gap-2 bg-success/10 text-success px-4 py-2 rounded-2xl border border-success/20 animate-fadeIn shrink-0">
            <CheckCircle size={18} />
            <span className="text-sm font-bold font-headline uppercase tracking-wide">
              {t(copy.materialDetail.learned)}
            </span>
          </div>
        )}
      </div>

      {/* Live Tatap Muka (Google Meet / Zoom) Banner */}
      {(material.meetingUrl || sessionHierarchy?.session?.meetingUrl) && (
        <div className="bg-gradient-to-r from-primary/15 via-primary/5 to-surface-container-low p-5 md:p-6 rounded-3xl border border-primary/20 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm animate-fadeIn">
          <div className="flex items-center gap-3.5">
            <div className="size-12 rounded-2xl bg-primary text-on-primary flex items-center justify-center shadow-level1 shrink-0">
              <Globe size={24} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-full border border-primary/20 flex items-center gap-1.5">
                  <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" /> Sesi Tatap
                  Muka Langsung
                </span>
              </div>
              <h3 className="font-headline font-bold text-on-surface text-base md:text-lg mt-0.5">
                Tatap Muka Online (Google Meet / Zoom)
              </h3>
              <p className="text-xs text-on-surface-variant/80">
                Silakan bergabung ke ruang tatap muka virtual untuk sesi diskusi atau kajian
                interaktif bersama pengajar.
              </p>
            </div>
          </div>

          <a
            href={material.meetingUrl || sessionHierarchy?.session?.meetingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-primary hover:bg-primary-container text-on-primary hover:text-on-primary-container font-bold text-xs shadow-level1 transition-all no-underline shrink-0"
          >
            <span>Gabung Tatap Muka</span>
            <ExternalLink size={15} />
          </a>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content Column */}
        <div className="lg:col-span-2 space-y-8">
          {/* Media Player Glass Card */}
          <div className="bg-surface-container-lowest rounded-[2rem] shadow-level1 overflow-hidden border border-on-surface/5 flex flex-col">
            <MediaPlayer material={material} />

            {/* Completion Bar - Subtle Progress Tracking */}
            {!isCompleted && (
              <div className="px-8 py-6 bg-surface-container-high/30 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-xl text-primary">
                    <Sparkles size={20} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-on-surface uppercase tracking-tight">
                      {t(copy.materialDetail.milestoneReady)}
                    </p>
                    <p className="text-[10px] text-on-surface-variant italic">
                      {material.type === 'VIDEO'
                        ? t(copy.materialDetail.finishVideo)
                        : material.type === 'AUDIO'
                          ? t(copy.materialDetail.listenAudio)
                          : t(copy.materialDetail.completeContent)}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleComplete}
                  disabled={completePending || !currentEnrollment}
                  className="gap-2 shadow-lg"
                >
                  {completePending ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <CheckCircle2 size={18} />
                  )}
                  {t(copy.materialDetail.iHaveLearned)}
                </Button>
              </div>
            )}
          </div>

          {/* Description Section */}
          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-1 h-6 bg-primary rounded-full" />
              <h2 className="text-xl font-headline font-semibold text-on-surface">
                {t(copy.materialDetail.materialContext)}
              </h2>
            </div>
            <div className="bg-surface-container-lowest rounded-3xl p-8 border border-on-surface/5 leading-relaxed text-on-surface-variant font-body">
              {material.content || t(copy.materialDetail.defaultContent)}
            </div>
          </section>

          {/* Reflection Loop Section */}
          {(showReflectionForm || isCompleted) && (
            <section className="animate-slideInUp space-y-4 pt-4">
              <div className="flex items-center gap-2 text-primary">
                <MessageSquare size={24} />
                <h2 className="text-xl font-headline font-bold">
                  {t(copy.materialDetail.reflectionLoop)}
                </h2>
              </div>

              <div className="bg-surface-container-lowest rounded-[2rem] p-8 border-2 border-primary/10 shadow-level1 space-y-6">
                {isCompleted && !showReflectionForm && (
                  <div className="text-sm font-body text-on-surface-variant text-center py-4 bg-primary/5 rounded-2xl border border-dashed border-primary/20 italic">
                    {t(copy.materialDetail.reflectionSolidify)}
                  </div>
                )}

                <div className="space-y-2">
                  <p className="text-sm font-bold text-on-surface">
                    {t(copy.materialDetail.keyTakeaways)}
                  </p>
                  <div className="relative">
                    <textarea
                      className="w-full bg-surface-container-high rounded-2xl p-6 text-sm font-body outline-none focus:ring-4 focus:ring-primary/10 min-h-[160px] resize-none border border-on-surface/5 transition-all"
                      placeholder={t(copy.materialDetail.takeawaysPlaceholder)}
                      value={reflectionText}
                      onChange={e => setReflectionText(e.target.value)}
                    />
                    <div className="absolute bottom-4 right-4 italic text-[10px] text-on-surface-variant opacity-40">
                      {reflectionText.length} {t(copy.materialDetail.characters)}
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 text-[10px] text-on-surface-variant uppercase tracking-widest font-bold">
                    <AlertCircle size={14} className="text-secondary" />
                    {t(copy.materialDetail.mentorsOnly)}
                  </div>
                  <Button
                    onClick={handleReflectionSubmit}
                    disabled={!reflectionText.trim() || submittingReflection}
                    className="gap-2 px-8 shadow-level2"
                  >
                    {submittingReflection ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Send size={18} />
                    )}
                    {t(copy.materialDetail.submitReflection)}
                  </Button>
                </div>
              </div>
            </section>
          )}
        </div>

        {/* Sidebar Space */}
        <aside className="space-y-6">
          {/* Summary Stats */}
          <div className="bg-surface-container-lowest rounded-3xl p-6 shadow-level1 border border-on-surface/5 space-y-6">
            <div>
              <p className="text-[10px] font-label text-on-surface-variant uppercase tracking-[0.2em] mb-4 font-bold">
                {t(copy.materialDetail.curriculumContext)}
              </p>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-body text-on-surface-variant">
                    {t(copy.materialDetail.estimated)}
                  </span>
                  <span className="text-xs font-bold text-on-surface">
                    {material.duration || 15} {t(copy.materialDetail.minRead)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-body text-on-surface-variant">
                    {t(copy.materialDetail.component)}
                  </span>
                  <span className="text-[10px] font-bold text-secondary bg-secondary/5 px-2 py-0.5 rounded uppercase tracking-tighter">
                    {t(copy.materialDetail.essential)}
                  </span>
                </div>
              </div>
            </div>

            {material.evaluations && material.evaluations.length > 0 && (
              <div className="pt-6 border-t border-on-surface/5 space-y-4">
                <p className="text-[10px] font-label text-on-surface-variant uppercase tracking-[0.2em] font-bold">
                  {t(copy.materialDetail.assessments)}
                </p>
                <div className="space-y-2">
                  {material.evaluations.map(ev => (
                    <Link key={ev.id} to={`/evaluations/${ev.id}`} className="block no-underline">
                      <div className="p-4 bg-secondary-container/30 rounded-2xl flex items-center justify-between group hover:bg-secondary-container transition-colors border border-secondary/10">
                        <div className="flex items-center gap-3">
                          <FileText size={18} className="text-secondary" />
                          <span className="text-xs font-bold text-on-secondary-container">
                            {ev.title}
                          </span>
                        </div>
                        <ArrowRight
                          size={16}
                          className="text-secondary opacity-30 group-hover:opacity-100 group-hover:translate-x-1 transition-all"
                        />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Media Repository / Attachments */}
          {material.media && material.media.length > 0 && (
            <div className="bg-surface-container-lowest rounded-3xl p-6 shadow-level1 border border-on-surface/5 space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-4 bg-primary rounded-full" />
                <p className="text-[10px] font-label text-on-surface-variant uppercase tracking-[0.2em] font-bold">
                  {t(copy.materialDetail.attachments)}
                </p>
              </div>
              <div className="space-y-3">
                {material.media
                  ?.sort((a: MaterialMedia, b: MaterialMedia) => (a.order || 0) - (b.order || 0))
                  .map((item: MaterialMedia) => {
                    const AssetIcon =
                      item.type === 'VIDEO'
                        ? Video
                        : item.type === 'AUDIO'
                          ? Music
                          : item.type === 'PDF'
                            ? FileText
                            : ExternalLink;

                    return (
                      <a
                        key={item.id}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-4 p-3 bg-surface-container-low/50 rounded-2xl border border-on-surface/5 group hover:bg-primary/5 hover:border-primary/20 transition-all no-underline"
                      >
                        <div className="flex-shrink-0 size-10 rounded-xl bg-surface-container flex items-center justify-center text-on-surface-variant group-hover:bg-primary group-hover:text-on-primary transition-all">
                          <AssetIcon size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-on-surface group-hover:text-primary transition-colors truncate">
                            {item.filename || item.title || t(copy.materialDetail.untitledAsset)}
                          </p>
                          <p className="text-[10px] text-on-surface-variant uppercase tracking-widest mt-0.5">
                            {item.type}
                          </p>
                        </div>
                        <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Download size={14} className="text-primary" />
                        </div>
                      </a>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Mentors Note Placeholder */}
          <div className="bg-primary/5 rounded-3xl p-6 border border-dashed border-primary/20">
            <h4 className="text-xs font-label uppercase tracking-widest text-primary mb-2 font-bold">
              {t(copy.materialDetail.noteFromAdmin)}
            </h4>
            <p className="text-xs italic text-on-surface-variant leading-relaxed">
              {t(copy.materialDetail.adminNoteText)}
            </p>
          </div>
        </aside>
      </div>

      {/* Bottom Session Next / Prev Navigation */}
      {sessionHierarchy && (
        <div className="mt-8 flex flex-col items-center justify-between gap-4 rounded-3xl bg-surface-container-low p-4 pt-5 sm:flex-row">
          {sessionHierarchy.previousMaterial ? (
            <Link
              to={`/materials/${sessionHierarchy.previousMaterial.id}`}
              aria-label={`${t(copy.materialDetail.prevMaterial)}: ${sessionHierarchy.previousMaterial.title}`}
              className="inline-flex w-full min-w-0 items-center gap-3 rounded-xl bg-surface-container-lowest px-4 py-3 text-on-surface no-underline shadow-level1 transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99] sm:max-w-[48%]"
            >
              <ArrowLeft size={16} className="shrink-0" />
              <span className="min-w-0 text-left">
                <span className="block text-[9px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                  {t(copy.materialDetail.prevMaterial)} · #
                  {sessionHierarchy.previousMaterialSession?.sessionNumber}
                </span>
                <span className="mt-1 block truncate text-xs font-bold">
                  {sessionHierarchy.previousMaterial.title}
                </span>
              </span>
            </Link>
          ) : (
            <div className="hidden sm:block" />
          )}

          {sessionHierarchy.followingMaterial ? (
            <Link
              to={`/materials/${sessionHierarchy.followingMaterial.id}`}
              aria-label={`${t(copy.materialDetail.nextMaterial)}: ${sessionHierarchy.followingMaterial.title}`}
              className="inline-flex w-full min-w-0 items-center justify-end gap-3 rounded-xl bg-primary px-4 py-3 text-on-primary no-underline shadow-level1 transition-colors hover:bg-primary-container focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99] sm:max-w-[48%]"
            >
              <span className="min-w-0 text-right">
                <span className="block text-[9px] font-label font-bold uppercase tracking-wider text-on-primary/75">
                  {t(copy.materialDetail.nextMaterial)} · #
                  {sessionHierarchy.followingMaterialSession?.sessionNumber}
                </span>
                <span className="mt-1 block truncate text-xs font-bold">
                  {sessionHierarchy.followingMaterial.title}
                </span>
              </span>
              <ArrowRight size={16} className="shrink-0" />
            </Link>
          ) : (
            <Link
              to={curriculumReturnUrl}
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-secondary px-6 py-3.5 text-xs font-bold text-on-secondary no-underline shadow-level1 transition-colors hover:bg-secondary-container focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95 sm:w-auto"
            >
              <span>{t(copy.materialDetail.backToCurriculum)}</span>
              <ArrowRight size={16} />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
